// frontend/src/AccessibilityAudit/SiteReport.jsx — one website's tab (spec 2.1): header with scan controls and the
// Device switch, severity tiles and trend, filters, the issue list, the WCAG checklist, Needs manual review and
// Pages scanned. Filters are applied here; the API sends the whole latest scan (a few hundred elements at most).
import React, { useEffect, useMemo, useState } from 'react';
import { a11yApi, download } from './apiClient';
import Checklist from './Checklist';
import IssueRow, { SEV, SEV_LABEL } from './IssueRow';

const TYPE_LABEL = { home: 'Home', collection: 'Collection', product: 'Product', cart: 'Cart', search: 'Search', page: 'Page',
  contact: 'Contact', blog: 'Blog', menu: 'Phone menu (open)' };
const fmtWhen = (iso) => (iso ? `${new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} ET` : '');
const total = (c) => (c ? SEV.reduce((n, s) => n + (c[s] || 0), 0) : 0);

function Trend({ history }) {
  const pts = history.filter((h) => h.counts).slice(-10).map((h) => total(h.counts.both));
  if (pts.length < 2) {
    return <><svg viewBox="0 0 200 34" role="img" aria-label="One scan so far"><line x1="0" y1="30" x2="200" y2="30" stroke="var(--uh-border)" /><circle cx="190" cy="12" r="4" fill="var(--uh-primary)" /></svg>
      <span className="sub">First scan. The trend line and changes since the last scan start with the second scan.</span></>;
  }
  const max = Math.max(...pts, 1); const step = 190 / (pts.length - 1);
  const xy = pts.map((v, i) => [5 + i * step, 30 - (v / max) * 24]);
  return <><svg viewBox="0 0 200 34" role="img" aria-label={`Failing elements over the last ${pts.length} scans: ${pts.join(', ')}`}>
    <polyline fill="none" stroke="var(--uh-primary)" strokeWidth="2" points={xy.map((p) => p.join(',')).join(' ')} />
    <circle cx={xy[xy.length - 1][0]} cy={xy[xy.length - 1][1]} r="3.5" fill="var(--uh-primary)" /></svg>
    <span className="sub">Failing elements, last {pts.length} scans</span></>;
}

function filterRules(list, f, typeOf) {
  const q = f.q.trim().toLowerCase();
  return (list || []).map((r) => ({
    ...r,
    els: r.elements.filter((e) => (f.device === 'both' || e.devices.includes(f.device))
      && (!f.type || e.pages.some((p) => typeOf[p] === f.type))
      && (!f.source || (f.source === 'theme' ? e.source === 'Theme' : e.source !== 'Theme'))
      && (!q || [r.help, r.id, e.selector, e.html].some((s) => (s || '').toLowerCase().includes(q)))),
  })).filter((r) => r.els.length && (!f.sev || r.impact === f.sev) && (!f.kind || r.kind === f.kind)
    && (!f.crit || r.wcag.some((w) => w.id === f.crit)));
}

export default function SiteReport({ site, running, busy, onScan, onCancel, reloadKey }) {
  const [rep, setRep] = useState(null);
  const [hist, setHist] = useState([]);
  const [ck, setCk] = useState(null);
  const [error, setError] = useState('');
  const [f, setF] = useState({ device: 'both', sev: '', type: '', source: '', kind: '', crit: '', q: '' });
  const set = (k, v) => setF((o) => ({ ...o, [k]: v }));

  useEffect(() => {
    let live = true;
    setRep(null); setError('');
    Promise.all([a11yApi.report(site.id), a11yApi.history(site.id)])
      .then(([r, h]) => { if (live) { setRep(r); setHist(h.scans); } }, (e) => live && setError(e.message));
    return () => { live = false; };
  }, [site.id, reloadKey]);
  useEffect(() => {
    let live = true;
    setCk(null);
    a11yApi.checklist(site.id, f.device).then((d) => live && setCk(d), () => {});
    return () => { live = false; };
  }, [site.id, f.device, reloadKey]);

  const typeOf = useMemo(() => {
    const m = {}; (rep && rep.pages ? rep.pages : []).forEach((p) => { if (p.type !== 'menu') m[p.path] = p.type; }); return m;
  }, [rep]);
  const scan = rep && rep.scan;
  const base = (scan && scan.base_url) || '';
  const rules = useMemo(() => (rep && rep.rules ? filterRules(rep.rules, f, typeOf) : []), [rep, f, typeOf]);
  const review = useMemo(() => (rep && rep.review ? filterRules(rep.review, { ...f, sev: '' }, typeOf) : []), [rep, f, typeOf]);
  const crits = useMemo(() => {
    const m = new Map(); (rep && rep.rules ? rep.rules : []).forEach((r) => r.wcag.forEach((w) => m.set(w.id, w.name)));
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));
  }, [rep]);
  const mine = running.find((s) => s.site_id === site.id);
  const other = !mine && running.find((s) => s.status === 'running');

  const header = (
    <div className="uh-card">
      <div className="uh-ax-bar">
        <div>
          <h2>{site.title}</h2>
          <div className="uh-ax-meta">
            {base ? <a href={base} target="_blank" rel="noopener noreferrer">{base.replace(/^https?:\/\//, '')} ↗</a> : <span>{site.domain}</span>}
            {scan ? <>
              <span>Last scan: {fmtWhen(scan.finished_at || scan.started_at)}{scan.status === 'cancelled' ? ' (cancelled partway)' : ''}</span>
              <span>{new Set((rep.pages || []).filter((p) => p.type !== 'menu').map((p) => p.path)).size} pages × {(scan.devices || []).length} devices</span>
              <span>axe-core {scan.axe_version}</span><span>WCAG 2.1 AA</span>
            </> : <span>Not scanned yet</span>}
          </div>
        </div>
        <div className="uh-ax-actions">
          {scan && <div className="uh-ax-seg" role="group" aria-label="Device">
            {[['both', 'Both'], ['desktop', 'Desktop'], ['phone', 'Phone']].map(([v, t]) => <button type="button" key={v} aria-pressed={f.device === v} onClick={() => set('device', v)}>{t}</button>)}
          </div>}
          {scan && <button type="button" className="uh-btn" onClick={() => a11yApi.csv(site.id).then(download, (e) => setError(e.message))}>Download CSV</button>}
          <button type="button" className="uh-btn primary" disabled={busy || !site.has_token} onClick={() => onScan(site.id)}
            title={site.has_token ? '' : `No token for ${site.domain}`}>Scan now</button>
        </div>
      </div>
      {mine && (
        <div className="uh-ax-progress">
          <span>{mine.status === 'queued' ? 'Queued: waits for the scan ahead of it.'
            : `Scanning ${Math.min(mine.pages_done + 1, mine.pages_total || 1)} of ${mine.pages_total || '…'} pages${mine.progress ? ` · ${mine.progress}` : ''}…`}</span>
          {mine.status === 'running' && <div className="track"><i style={{ width: `${Math.round(100 * (mine.pages_done || 0) / (mine.pages_total || 1))}%` }} /></div>}
          <button type="button" className="uh-btn slim" onClick={() => onCancel(mine.id)}>Cancel</button>
        </div>
      )}
      {other && <div className="uh-ax-progress"><span>A scan of another website is running: Scan now is off until it finishes.</span></div>}
      {!site.has_token && <div className="uh-ax-notes uh-err">No token for {site.domain} in shopify_stores.json, so this website can't be scanned and Scan all skips it.</div>}
      {scan && scan.notes && scan.notes.length > 0 && <div className="uh-ax-notes">{scan.notes.join(' ')}</div>}
      {site.last && site.last.status === 'failed' && (!scan || site.last.id > scan.id) && <div className="uh-ax-notes uh-err">The last scan failed: {site.last.error}</div>}
    </div>
  );

  if (error) return <>{header}<div className="uh-alert crit"><div>{error}</div></div></>;
  if (!rep) return <>{header}<div className="uh-card"><div className="uh-empty">Loading…</div></div></>;
  if (!scan) {
    return <>{header}<div className="uh-card"><div className="uh-empty">{site.title} hasn't been scanned yet. {site.has_token ? 'Scan now runs about 10 sample pages on desktop and phone (20 to 30 minutes).' : ''}</div></div></>;
  }

  const c = (rep.counts && rep.counts[f.device]) || {};
  const pc = rep.previous && rep.previous.counts && rep.previous.counts[f.device];
  const shownEls = rules.reduce((n, r) => n + r.els.length, 0);
  const pagesShown = (rep.pages || []).filter((p) => f.device === 'both' || p.device === f.device);
  const failedPages = pagesShown.filter((p) => p.error).length;
  const types = [...new Set(Object.values(typeOf))];

  return (
    <>
      {header}
      <div className="uh-ax-tiles">
        {SEV.map((s) => {
          const d = pc ? (c[s] || 0) - (pc[s] || 0) : null;
          return (
            <div key={s} className={`uh-card uh-ax-tile sev-${s}`}>
              <span className="lbl">{SEV_LABEL[s]}</span><span className="num">{c[s] || 0}</span>
              <span className="sub">elements{d ? ` · ${d > 0 ? '▲' : '▼'} ${Math.abs(d)} since last scan` : pc ? ' · no change' : ''}</span>
            </div>
          );
        })}
        <div className="uh-card uh-ax-tile trend"><span className="lbl">Trend</span><Trend history={hist} /></div>
      </div>

      <div className="uh-card">
        <div className="uh-ax-filters">
          <select aria-label="Severity" value={f.sev} onChange={(e) => set('sev', e.target.value)}>
            <option value="">All severities</option>{SEV.map((s) => <option key={s} value={s}>{SEV_LABEL[s]}</option>)}
          </select>
          <select aria-label="Page type" value={f.type} onChange={(e) => set('type', e.target.value)}>
            <option value="">All page types</option>{types.map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}
          </select>
          <select aria-label="WCAG criterion" value={f.crit} onChange={(e) => set('crit', e.target.value)}>
            <option value="">All WCAG criteria</option>{crits.map(([id, name]) => <option key={id} value={id}>{id} {name}</option>)}
          </select>
          <select aria-label="Source" value={f.source} onChange={(e) => set('source', e.target.value)}>
            <option value="">Theme and apps</option><option value="theme">Theme only</option><option value="app">Apps only</option>
          </select>
          <select aria-label="Check type" value={f.kind} onChange={(e) => set('kind', e.target.value)}>
            <option value="">axe and interaction checks</option><option value="axe">axe only</option><option value="interaction">Interaction checks only</option>
          </select>
          <input type="search" aria-label="Search" placeholder="Search rule, selector or HTML" value={f.q} onChange={(e) => set('q', e.target.value)} />
          <span className="count">{rules.length} issues · {shownEls} elements</span>
        </div>
        <div className="uh-ax-rules">
          {rules.length ? rules.map((r) => <IssueRow key={r.id} rule={r} device={f.device} base={base} />)
            : <div className="uh-empty">{(rep.rules || []).length ? 'No issues match these filters. Clear a filter or switch the device to Both.' : 'No failing elements in the latest scan.'}</div>}
        </div>
      </div>

      <Checklist data={ck} device={f.device} />

      <details className="uh-card uh-ax-sec">
        <summary>Needs manual review <span>{review.reduce((n, r) => n + r.els.length, 0)} elements the checks could not decide</span></summary>
        <p className="uh-ax-intro">axe and the interaction checks found these but couldn't tell whether they pass, for example text over a background image, where contrast can't be measured. A person has to look at each one.</p>
        <div className="uh-ax-rules">{review.length ? review.map((r) => <IssueRow key={r.id} rule={r} device={f.device} base={base} review />) : <div className="uh-empty">Nothing to review on these pages.</div>}</div>
      </details>

      <details className="uh-card uh-ax-sec">
        <summary>Pages scanned <span>{pagesShown.length} page scans{failedPages ? `, ${failedPages} failed` : ''}</span></summary>
        <div className="uh-tscroll uh-ax-tbl"><table className="uh-tbl">
          <thead><tr><th>Page</th><th>Type</th><th>Device</th><th className="r">Status</th><th className="r">Elements</th><th>Problem</th></tr></thead>
          <tbody>{pagesShown.map((p) => (
            <tr key={p.id}>
              <td><a href={p.url} target="_blank" rel="noopener noreferrer">{p.path}</a></td><td>{TYPE_LABEL[p.type] || p.type}</td>
              <td>{p.device === 'phone' ? 'Phone' : 'Desktop'}</td><td className="r uh-num">{p.status ?? '–'}</td>
              <td className="r uh-num">{p.error ? '–' : p.elements}</td>
              <td className="err">{p.error || (p.checks_failed.length ? `${p.checks_failed.length} interaction check${p.checks_failed.length === 1 ? '' : 's'} couldn't run` : '')}</td>
            </tr>))}</tbody>
        </table></div>
      </details>
    </>
  );
}
